import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

/** Virtual-scroll dual-list column with native-multiselect-like keyboard support. */
@Component({
  selector: 'app-query-virtual-dual-list',
  standalone: true,
  imports: [CommonModule, ScrollingModule],
  template: `
    <div
      #listbox
      class="virtual-dual-listbox virtual-dual-viewport"
      role="listbox"
      [attr.aria-label]="ariaLabel"
      aria-multiselectable="true"
      [attr.aria-activedescendant]="activeDescendantId"
      tabindex="0"
      (focus)="onListboxFocus()"
      (keydown)="onKeydown($event)"
    >
      <cdk-virtual-scroll-viewport [itemSize]="itemSizePx" class="virtual-dual-viewport-inner">
        <div
          *cdkVirtualFor="let o of options; trackBy: trackByOption; let i = index"
          [id]="optionId(o.value)"
          role="option"
          [attr.aria-selected]="selection.has(o.value)"
          class="virtual-dual-row"
          [class.virtual-dual-row--selected]="selection.has(o.value)"
          [class.virtual-dual-row--focused]="isFocused(i)"
          [title]="o.label"
          (click)="onRowClick($event, i, o.value)"
        >
          {{ o.label }}
        </div>
      </cdk-virtual-scroll-viewport>
    </div>
  `,
  styles: [
    `
      .virtual-dual-viewport {
        height: 220px;
        width: 100%;
        min-width: 200px;
        border: 1px solid var(--sed-black-60-color);
        border-radius: 0.25rem;
        background: var(--sed-warm-white-color, #fff);
        box-sizing: border-box;
      }

      .virtual-dual-viewport:focus {
        outline: 2px solid var(--sed-engaged-black-color);
        outline-offset: 0;
      }

      .virtual-dual-viewport-inner {
        height: 100%;
        width: 100%;
      }

      .virtual-dual-row {
        padding: 0.25rem 0.5rem;
        font-size: 0.875rem;
        line-height: 1.25rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        cursor: default;
        user-select: none;
        box-sizing: border-box;
      }

      .virtual-dual-row--selected {
        background: var(--sed-grey-100-color);
        font-weight: 600;
      }

      .virtual-dual-row--focused {
        box-shadow: inset 0 0 0 1px var(--sed-engaged-black-color);
      }
    `,
  ],
})
export class QueryVirtualDualListComponent implements OnChanges {
  @Input({ required: true }) options: { label: string; value: string }[] = [];
  @Input({ required: true }) selection!: Set<string>;
  @Input({ required: true }) ariaLabel!: string;
  @Input() listId = 'virtual-dual-list';

  @ViewChild(CdkVirtualScrollViewport)
  private viewport?: CdkVirtualScrollViewport;

  @ViewChild('listbox')
  private listbox?: ElementRef<HTMLElement>;

  readonly itemSizePx = 28;
  focusedIndex = -1;
  private rangeAnchorIndex = -1;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.clampFocusedIndex();
    }
  }

  trackByOption = (_: number, o: { value: string }): string => o.value;

  get activeDescendantId(): string | null {
    if (this.focusedIndex < 0 || this.focusedIndex >= this.options.length) {
      return null;
    }
    return this.optionId(this.options[this.focusedIndex].value);
  }

  isFocused(index: number): boolean {
    return index === this.focusedIndex;
  }

  optionId(value: string): string {
    return `${this.listId}-opt-${value}`;
  }

  onListboxFocus(): void {
    if (this.options.length === 0) {
      this.focusedIndex = -1;
      this.rangeAnchorIndex = -1;
      return;
    }
    if (this.focusedIndex < 0 || this.focusedIndex >= this.options.length) {
      const selectedIndex = this.options.findIndex((o) => this.selection.has(o.value));
      this.focusedIndex = selectedIndex >= 0 ? selectedIndex : 0;
      this.rangeAnchorIndex = this.focusedIndex;
      if (this.selection.size === 0) {
        const value = this.options[this.focusedIndex]?.value;
        if (value) {
          this.selectSingle(value);
        }
      }
    }
    this.scrollToFocused(true);
  }

  onRowClick(event: MouseEvent, index: number, value: string): void {
    this.listbox?.nativeElement.focus();
    if (event.ctrlKey || event.metaKey) {
      this.toggleValue(value);
    } else if (event.shiftKey) {
      this.selectRange(index);
    } else {
      this.selectSingle(value);
      this.rangeAnchorIndex = index;
    }
    this.focusedIndex = index;
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.options.length === 0) {
      return;
    }
    if (this.focusedIndex < 0) {
      this.onListboxFocus();
    }

    const ctrl = event.ctrlKey || event.metaKey;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveFocus(1, event.shiftKey, ctrl);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveFocus(-1, event.shiftKey, ctrl);
        break;
      case 'Home':
        event.preventDefault();
        this.applyFocusIndex(0, event.shiftKey, ctrl);
        break;
      case 'End':
        event.preventDefault();
        this.applyFocusIndex(this.options.length - 1, event.shiftKey, ctrl);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (ctrl) {
          this.toggleFocused();
        } else {
          const value = this.options[this.focusedIndex]?.value;
          if (value) {
            this.selectSingle(value);
            this.rangeAnchorIndex = this.focusedIndex;
          }
        }
        break;
      case 'a':
      case 'A':
        if (ctrl) {
          event.preventDefault();
          this.selectAll();
        }
        break;
      default:
        break;
    }
  }

  private moveFocus(delta: number, shift: boolean, ctrl: boolean): void {
    const start = this.focusedIndex < 0 ? 0 : this.focusedIndex;
    const next = Math.max(0, Math.min(this.options.length - 1, start + delta));
    this.applyFocusIndex(next, shift, ctrl);
  }

  private applyFocusIndex(index: number, shift: boolean, ctrl: boolean): void {
    this.focusedIndex = index;
    if (shift) {
      this.selectRange(index);
    } else if (ctrl) {
      this.scrollToFocused();
      return;
    } else {
      const value = this.options[index]?.value;
      if (value) {
        this.selectSingle(value);
        this.rangeAnchorIndex = index;
      }
    }
    this.scrollToFocused();
  }

  private selectSingle(value: string): void {
    this.selection.clear();
    this.selection.add(value);
  }

  private toggleValue(value: string): void {
    if (this.selection.has(value)) {
      this.selection.delete(value);
    } else {
      this.selection.add(value);
    }
  }

  private toggleFocused(): void {
    const value = this.options[this.focusedIndex]?.value;
    if (value) {
      this.toggleValue(value);
    }
  }

  private selectRange(toIndex: number): void {
    if (this.rangeAnchorIndex < 0) {
      this.rangeAnchorIndex = toIndex;
    }
    const start = Math.min(this.rangeAnchorIndex, toIndex);
    const end = Math.max(this.rangeAnchorIndex, toIndex);
    this.selection.clear();
    for (let i = start; i <= end; i++) {
      this.selection.add(this.options[i].value);
    }
    this.focusedIndex = toIndex;
  }

  private selectAll(): void {
    this.selection.clear();
    for (const o of this.options) {
      this.selection.add(o.value);
    }
  }

  private clampFocusedIndex(): void {
    if (this.options.length === 0) {
      this.focusedIndex = -1;
      this.rangeAnchorIndex = -1;
      return;
    }
    if (this.focusedIndex >= this.options.length) {
      this.focusedIndex = this.options.length - 1;
    }
  }

  /** Scroll only when the focused row would leave the viewport (native select behavior). */
  private scrollToFocused(refreshView = false): void {
    if (this.focusedIndex < 0) {
      return;
    }
    queueMicrotask(() => {
      const viewport = this.viewport;
      if (viewport) {
        const itemSize = this.itemSizePx;
        const visibleCount = Math.max(1, Math.floor(viewport.getViewportSize() / itemSize));
        const scrollTop = viewport.measureScrollOffset('top');
        const firstVisible = Math.floor(scrollTop / itemSize);
        const lastVisible = firstVisible + visibleCount - 1;

        if (this.focusedIndex < firstVisible) {
          viewport.scrollToOffset(this.focusedIndex * itemSize, 'auto');
        } else if (this.focusedIndex > lastVisible) {
          viewport.scrollToOffset((this.focusedIndex - visibleCount + 1) * itemSize, 'auto');
        }
      }
      if (refreshView) {
        this.cdr.detectChanges();
      }
    });
  }
}
